import {
  AnalyticsIntegrations,
  fireAnalyticsEvent
} from '@dtx-company/inter-app/src/event-tracking/helpers/fireAnalyticsEvent'
import { Button, IconButton, Stack, Typography, themeFlow2 } from '@dtx-company/design-system/src'
import { F2UpsellHeapId, F2UpsellHubspotEvent } from './F2Upsell.const'
import { F2_UPSELL_BANNER_ASSETS_PAGE } from '@app/analytics/src/constants/optimizely'
import { FC, useEffect, useState } from 'react'
import { LlmFlowcodeApiResponse } from '../pages/CodeeLLM/api/llmAPI.types'
import { SLACK_F2_UPSELL_ENDPOINT } from '@dtx-company/true-common/src/constants/endpoints'
import { ThemeProvider } from '@mui/material/styles'
import { TopNavBannerWrapper } from '@app/code/src/components/Homepage/TopNavBannerWrapper/TopNavBannerWrapper'
import { flowFetch } from '@app/code/src/utils/flowFetch'
import { useAuthState } from '@dtx-company/inter-app/src/hooks/useAuthState'
import { useF2UpsellFeatureConfig } from './useF2UpsellFeatureConfig'
import { useOnce } from '@dtx-company/true-common/src/hooks/useOnce'
import { usePathname } from 'next/navigation'
import { useScreenSizeBreakpoints } from '@app/common/src/hooks/useScreenSizeBreakpoints'
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined'
import TipsAndUpdatesOutlinedIcon from '@mui/icons-material/TipsAndUpdatesOutlined'

export const F2UpsellBanner: FC<{ onCloseModal?: () => void }> = ({ onCloseModal }) => {
  useOnce(() => {
    fireAnalyticsEvent(F2UpsellHeapId + '_Assets_Banner_View')
  })
  const { jwt } = useAuthState()
  const pathname = usePathname()
  const { isDesktop } = useScreenSizeBreakpoints()
  const { onClose } = useF2UpsellFeatureConfig(F2_UPSELL_BANNER_ASSETS_PAGE)
  const [getDemoClicked, setGetDemoClicked] = useState(false)
  useEffect(() => {
    return () => {
      if (getDemoClicked) onClose()
    }
  }, [getDemoClicked, onClose])
  const onGetDemoClicked = async (): Promise<void> => {
    const body = JSON.stringify({
      text: `${process.env.APP_ENV === 'production' ? '' : 'TEST. IGNORE ME. '}User with email ${
        jwt?.email
      } has requested more information about Flowcode 2 from the ${pathname} page banner`
    })
    fireAnalyticsEvent(F2UpsellHeapId + '_Assets_Banner_Get_Demo_Clicked')
    fireAnalyticsEvent(
      F2UpsellHubspotEvent,
      {
        email: jwt?.email
      },
      [AnalyticsIntegrations.HUBSPOT]
    )
    try {
      // send a message to #f2-upgrade-requests channel
      await flowFetch<LlmFlowcodeApiResponse>(
        'POST',
        SLACK_F2_UPSELL_ENDPOINT + '/send',
        {
          team: 'PLG'
        },
        {
          body
        }
      )
    } catch (error) {
      fireAnalyticsEvent(F2UpsellHeapId + '_Assets_Banner_Get_Demo_Clicked_Error', {
        email: jwt?.email
      })
    }
    setGetDemoClicked(true)
  }

  return (
    <ThemeProvider theme={themeFlow2}>
      <TopNavBannerWrapper
        data-testid="upsell-banner"
        positionRelative={false}
        backgroundColor={'tertiary.main'}
      >
        <Stack
          direction={'row'}
          gap={2}
          justifyContent={'space-between'}
          alignItems={'center'}
          sx={{ width: '100%', px: 4 }}
        >
          <Stack direction={'row'} gap={4} sx={{ color: 'white' }}>
            <TipsAndUpdatesOutlinedIcon />
            <Typography>
              {isDesktop &&
                getDemoClicked &&
                'You’re all set! A member of our team will reach out to you soon to schedule a demo!'}
              {isDesktop &&
                !getDemoClicked &&
                'Flowcode 2, our newest platform, features retargeting capabilities and audience metrics'}
              {!isDesktop && getDemoClicked && 'We’ll talk soon!'}
              {!isDesktop && !getDemoClicked && 'Try Flowcode 2'}
            </Typography>
          </Stack>
          <Stack gap={4} direction={'row'} alignItems={'center'}>
            <Stack
              sx={{
                '& button': { backgroundColor: 'white' },
                '& button:hover, & button:focus': { backgroundColor: 'white' },
                '& button:disabled': {
                  backgroundColor: 'surface.3',
                  opacity: 1,
                  color: 'text.secondary'
                }
              }}
            >
              <Button
                color={'secondary'}
                onClick={onGetDemoClicked}
                size={'small'}
                disabled={getDemoClicked}
              >
                {getDemoClicked ? 'Thanks!' : 'Get a Demo'}
              </Button>
            </Stack>
            <Stack sx={{ '& button': { color: 'white' } }}>
              <IconButton
                icon={CloseOutlinedIcon}
                variant={'text'}
                aria-label={'Close'}
                onClick={() => {
                  fireAnalyticsEvent(F2UpsellHeapId + '_Assets_Banner_Closed')
                  onClose()
                  onCloseModal?.()
                }}
              />
            </Stack>
          </Stack>
        </Stack>
      </TopNavBannerWrapper>
    </ThemeProvider>
  )
}
